import { useMemo } from "react";

import ActionButton from "./ActionButton";
import OverflowMenu from "./OverflowMenu";
import type { ResponsiveActionsProps } from "./types";
import { partitionActions } from "./utils";

import { useTruncatedList } from "@/hooks/useTruncatedList";
import { cn } from "@/lib/utils";

const GAP_PX = 8;
const OVERFLOW_MENU_WIDTH_PX = 32;

const ResponsiveActions = ({ actions, className, menuAlign = "end" }: ResponsiveActionsProps) => {
  const visibleActions = useMemo(() => actions.filter(Boolean), [actions]);
  const { pinnedActions, overflowActions } = useMemo(
    () => partitionActions(visibleActions),
    [visibleActions],
  );

  // Pinned first so useTruncatedList prefers keeping alwaysVisible actions.
  const measureActions = useMemo(
    () => [...pinnedActions, ...overflowActions],
    [pinnedActions, overflowActions],
  );

  const recalcKey = useMemo(
    () =>
      measureActions
        .map(action => `${action.id}:${action.label}:${action.alwaysVisible ? "1" : "0"}`)
        .join("|"),
    [measureActions],
  );

  const { containerRef, measureRef, visibleCount, hiddenCount } = useTruncatedList({
    itemCount: measureActions.length,
    gapPx: GAP_PX,
    overflowIndicatorWidth: OVERFLOW_MENU_WIDTH_PX,
    recalcKey,
  });

  const visibleOverflowCount = Math.max(0, visibleCount - pinnedActions.length);
  const visibleOverflowIds = useMemo(
    () => new Set(overflowActions.slice(0, visibleOverflowCount).map(action => action.id)),
    [overflowActions, visibleOverflowCount],
  );
  const menuActions = useMemo(
    () => overflowActions.filter(action => !visibleOverflowIds.has(action.id)),
    [overflowActions, visibleOverflowIds],
  );

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className={cn("relative flex w-full min-w-0 items-center justify-end gap-2", className)}
    >
      <div
        ref={measureRef}
        className="pointer-events-none invisible absolute left-0 top-0 flex w-max items-center gap-2"
        aria-hidden
      >
        {measureActions.map(action => (
          <ActionButton key={action.id} action={action} />
        ))}
      </div>

      {visibleActions.map(action => {
        if (action.alwaysVisible) {
          return <ActionButton key={action.id} action={action} />;
        }
        if (hiddenCount === 0 || visibleOverflowIds.has(action.id)) {
          return <ActionButton key={action.id} action={action} />;
        }
        return null;
      })}
      {menuActions.length > 0 ? <OverflowMenu actions={menuActions} align={menuAlign} /> : null}
    </div>
  );
};

export default ResponsiveActions;
export type { ResponsiveAction, ResponsiveActionsProps } from "./types";
